import { useMemo } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart3, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCompactTime } from "@/lib/timeUtils";
import { startOfWeek, endOfWeek, format } from "date-fns";
import { useReportTileEmpty } from "./DashboardTile";

export default function TeamEffortSnapshotWidget() {
  const [, navigate] = useLocation();
  const { managedTeamIds, leadTeamIds } = useAuth();

  // Get current week range
  const now = new Date();
  const weekStart = format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd');
  const weekEnd = format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd');

  // Fetch effort comparison data for managed teams
  const teamIds = useMemo(() => Array.from(new Set([...managedTeamIds, ...leadTeamIds])), [managedTeamIds, leadTeamIds]);

  const { data: effortData, isLoading } = useQuery<any>({
    queryKey: ["/api/analytics/effort-comparison", { startDate: weekStart, endDate: weekEnd }],
    enabled: teamIds.length > 0,
  });

  // Parse effort into per-user summary
  const members = useMemo(() => {
    if (!effortData?.users) return [];
    return (effortData.users as any[])
      .map((u: any) => ({
        name: u.name || u.username || 'Unknown',
        logged: Number(u.totalHours || u.logged || 0),
        expected: Number(u.expectedHours || u.expected || 40),
      }))
      .sort((a: any, b: any) => b.logged - a.logged)
      .slice(0, 8);
  }, [effortData]);

  useReportTileEmpty("team_effort_snapshot", !isLoading && (members?.length ?? 0) === 0);

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 shadow-md">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <CardTitle className="text-lg font-bold">Team Effort</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 gap-1 font-semibold h-8 px-2"
            onClick={() => navigate("/effort-report")}
          >
            Full Report <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        {members.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No effort data this week</p>
          </div>
        ) : (
          <div className="space-y-3">
            {members.map((m, i) => {
              const percent = m.expected > 0 ? Math.min(100, Math.round((m.logged / m.expected) * 100)) : 0;
              const barColor = percent >= 80 ? "bg-emerald-500" : percent >= 50 ? "bg-amber-500" : "bg-rose-500";
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-foreground truncate max-w-[60%]">
                      {m.name}
                    </span>
                    <span className="text-xs text-muted-foreground font-medium">
                      {formatCompactTime(m.logged)} <span className="text-muted-foreground/60">/ {formatCompactTime(m.expected)}</span>
                    </span>
                  </div>
                  <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn("absolute inset-y-0 left-0 rounded-full transition-all duration-500", barColor)}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
